// src/shared/components/ProductGate.tsx
//
// Access to a workspace is a PURCHASE, not a side effect of which vertical an
// account happens to sit on. Analyze, Compliance, Crisis Ops and Creator are
// separate products; this gate asks the one question that matters — does this
// account own it — and the answer comes from the server's entitlement
// resolution (platform/entitlements), never from client state.
//
// When the answer is no it says so, on a real page with a way forward. A silent
// redirect to the dashboard leaves people wondering whether they mis-clicked.
import type { ReactNode } from "react";
import { Lock, ArrowRight } from "lucide-react";
import { useProduct } from "@/shared/contexts/ProductContext";
import { getProduct, type ProductId } from "@/platform/products";
import { readDesktopApp } from "@/shared/dashboard/desktopMode";

const go = (path: string) => (window as any).navigate?.(path);

export function ProductGate({
  product,
  children,
}: {
  product?: ProductId;
  children: ReactNode;
}) {
  const { products, resolved, loading } = useProduct();

  // Ungated surface, or entitlements not resolved yet — render the page. The
  // server enforces the real boundary on every request, so an optimistic first
  // paint cannot leak anything.
  if (!product || (!resolved && loading) || products.includes(product)) {
    return <>{children}</>;
  }

  const meta = getProduct(product);
  const desktopApp = readDesktopApp();
  const surfaceName = desktopApp?.label ?? meta?.name ?? "This app";
  return (
    <main className="flex min-h-[70vh] flex-1 items-center justify-center px-6 py-16">
      <div className="max-w-md text-center">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-full border border-[rgba(12,26,51,0.14)] bg-[rgba(12,26,51,0.03)] text-[rgba(12,26,51,0.55)]">
          <Lock size={20} aria-hidden="true" />
        </span>
        <h1 className="ed-serif mt-5 text-[1.5rem] leading-snug text-[#0c1a33]">
          {surfaceName} isn’t part of your plan
        </h1>
        {meta?.tagline && !desktopApp && (
          <p className="mt-2 text-[14px] leading-relaxed text-[rgba(12,26,51,0.6)]">
            {meta.tagline}
          </p>
        )}
        <p className="mt-4 text-[13px] leading-relaxed text-[rgba(12,26,51,0.5)]">
          Your account keeps everything it already has. Ask your administrator
          or add access to open this app.
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => go("/tools")}
            className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-md bg-[#0c1a33] px-5 text-[14px] font-medium text-white transition-colors hover:bg-[#13243f]"
          >
            See what it does <ArrowRight size={15} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => go("/dashboard")}
            className="inline-flex min-h-11 cursor-pointer items-center rounded-md border border-[rgba(12,26,51,0.2)] px-5 text-[14px] text-[#0c1a33] transition-colors hover:bg-[rgba(12,26,51,0.04)]"
          >
            Back to your work
          </button>
        </div>
      </div>
    </main>
  );
}
